// components/ProductShowcaseCard.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react'; // Make sure lucide-react is installed

// data/livingRoomShowcase.js
export const livingRoomShowcaseItems = [
  {
    id: 1,
    title: 'Modern Sofas',
    description: 'Comfort and style for your living space.',
    image: '/assets/slide1.png',
    link: '/livingroom/sofas',
  },
  {
    id: 2,
    title: 'Accent Chairs',
    description: 'Stylish additions to any living area.',
    image: '/assets/mat_lea_2.jpg',
    link: '/livingroom/chairs',
  },
  {
    id: 3,
    title: 'Coffee Tables',
    description: 'Functional and aesthetic centerpiece.',
    image: '/assets/Gallery3.png',
    link: '/livingroom/coffee-tables',
  },
  {
    id: 4,
    title: 'Media Units',
    description: 'Sleek storage for your entertainment.',
    image: '/assets/mat_me_2.jpg',
    link: '/livingroom/media-units',
  },
];

export const bedroomShowcaseItems = [
  {
    id: 1,
    title: 'Fine Dining Chairs',
    description: 'Elegant seating for a luxurious experience.',
    image: '/assets/caf_fd_1.jpg',
    link: '/restaurant/fine-dining-chairs',
  },
  {
    id: 2,
    title: 'Dining Tables',
    description: 'Spacious and sophisticated dining tables.',
    image: '/assets/caf_fd_2.jpg',
    link: '/restaurant/dining-tables',
  },
  {
    id: 3,
    title: 'Lounge Area Furniture',
    description: 'Comfortable and chic lounge setups.',
    image: '/assets/caf_bst_1.jpg',
    link: '/restaurant/lounge-furniture',
  },
  {
    id: 4,
    title: 'Outdoor Restaurant Seating',
    description: 'Stylish and weather-resistant outdoor options.',
    image: '/assets/caf_otd_1.jpg',
    link: '/restaurant/outdoor-seating',
  },
];

// data/cafeShowcase.js
export const cafeShowcaseItems = [
  {
    id: 1,
    title: 'Cafe Seating',
    description: 'Cozy and inviting chairs for cafes.',
    image: '/assets/caf_caf_1.jpg',
    link: '/cafe/seating',
  },
  {
    id: 2,
    title: 'Bistro Tables',
    description: 'Compact and stylish tables.',
    image: '/assets/caf_bst_1.jpg',
    link: '/cafe/tables',
  },
  {
    id: 3,
    title: 'Outdoor Cafe Sets',
    description: 'Durable and charming outdoor furniture.',
    image: '/assets/caf_otd_1.jpg',
    link: '/cafe/outdoor',
  },
  {
    id: 4,
    title: 'Chairs',
    description: 'Modern designs for cafe bars.',
    image: '/assets/caf_caf_3.jpg',
    link: '/cafe/chairs',
  },
];

// data/restaurantShowcase.js
export const restaurantShowcaseItems = [
  {
    id: 1,
    title: 'Fine Dining Chairs',
    description: 'Elegant seating for a luxurious experience.',
    image: '/assets/caf_fd_2.jpg',
    link: '/restaurant/fine-dining-chairs',
  },
  {
    id: 2,
    title: 'Dining Tables',
    description: 'Spacious and sophisticated dining tables.',
    image: '/assets/caf_fd_3.jpg',
    link: '/restaurant/dining-tables',
  },
  {
    id: 3,
    title: 'Lounge Area Furniture',
    description: 'Comfortable and chic lounge setups.',
    image: '/assets/caf_bst_3.jpg',
    link: '/restaurant/lounge-furniture',
  },
  {
    id: 4,
    title: 'Outdoor Restaurant Seating',
    description: 'Stylish and weather-resistant outdoor options.',
    image: '/assets/caf_otd_2.jpg',
    link: '/restaurant/outdoor-seating',
  },
];

export const barShowcaseItems = [
  {
    id: 1,
    title: "Bar Stools & Chairs",
    description: "Elegant seating for your bar area.",
    image: "/assets/caf_bst_1.jpg",
    link: "/bar/stools-chairs",
  },
  {
    id: 2,
    title: "Bar Counters",
    description: "Stylish counters to impress your guests.",
    image: "/assets/caf_bst_2.jpg",
    link: "/bar/counters",
  },
  {
    id: 3,
    title: "Storage & Display",
    description: "Showcase your beverages with style.",
    image: "/assets/caf_bst_3.jpg",
    link: "/bar/storage-display",
  },
  {
    id: 4,
    title: "Bar Cabinets",
    description: "Secure and stylish storage options.",
    image: "/assets/caf_bst_4.jpg",
    link: "/bar/cabinets",
  },
  {
    id: 5,
    title: "Custom Solutions",
    description: "Tailor-made designs for unique bar spaces.",
    image: "/assets/mat_me_1.jpg",
    link: "/bar/custom-solutions",
  },
];

export const ProductShowcaseCard = ({ item }) => {
  const isGalleryImage = item.image.includes('Gallery');
  
  return (
    <div className="relative group bg-gray-100 rounded-lg shadow-md overflow-hidden transform transition-transform duration-300 hover:scale-105">
      <div className="relative overflow-hidden bg-gray-100">
        <div className="aspect-[4/5] w-full">
          <img
            src={item.image}
            alt={item.title}
            className={`absolute inset-0 w-full h-full object-cover ${isGalleryImage ? 'object-top' : ''}`}
            loading="lazy"
            onError={(e) => { 
              e.target.onerror = null; 
              e.target.src = `https://placehold.co/600x750/E5E7EB/6B7280?text=${encodeURIComponent(item.title)}`; 
            }}
            style={isGalleryImage ? { objectPosition: 'center 20%' } : {}}
          />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black via-black/50 to-transparent">
        <h3 className="text-lg sm:text-xl font-semibold text-white line-clamp-2">{item.title}</h3>
        <p className="mt-2 text-sm sm:text-base text-white line-clamp-2">{item.description}</p>
        <Link to={item.link} className="mt-4 text-yellow-400 flex items-center no-underline group-hover:text-yellow-300 transition-colors duration-200 text-sm sm:text-base">
          Explore Collection
          <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

// components/ProductShowcase.jsx
export const ProductShowcase = ({ title, items }) => {
  if (!items || items.length === 0) {
    return null; // Don't render if no items are provided
  }

  return (
    <section className="bg-white py-12">
      <div className="max-w-7xl mx-auto px-8">
        <h2 className="text-center text-3xl font-semibold mb-8 text-gray-800">{title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map((item) => (
            <ProductShowcaseCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;